import { useEffect, useRef, useState } from "react";

import { endView, isViewingAsCustomer, viewingEmail } from "@utils/viewAs";
import styled from "styled-components";

/**
 * The strip at the top of a tab that is showing a customer's account.
 *
 * An admin with several tabs open genuinely cannot tell whose account is in
 * front of them, and that is how someone quotes the wrong customer's data back
 * to them. So the account is named, permanently, with the way out beside it.
 *
 * It publishes its own height into `--view-as-banner-h`, the same mechanism the
 * global banners use, so the page below is pushed down instead of covered.
 */
const Bar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 6px 16px;
  background: #2f6fb0;
  color: #fff;
  font-size: 13px;
  line-height: 1.3;
`;

const Who = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  strong {
    font-weight: 600;
  }
`;

const Exit = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.5);
  background: transparent;
  color: #fff;
  border-radius: 4px;
  padding: 3px 10px;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const Ended = styled(Bar)`
  background: #4a4a4a;
  justify-content: center;
`;

export function ViewAsBar() {
  const [viewing, setViewing] = useState(isViewingAsCustomer());
  const [ended, setEnded] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  // The hour can run out mid-session; the request layer drops the view and
  // announces it rather than logging anybody out.
  useEffect(() => {
    const onEnded = () => {
      setViewing(false);
      setEnded(true);
    };
    window.addEventListener("as-view-ended", onEnded);
    return () => window.removeEventListener("as-view-ended", onEnded);
  }, []);

  // Publish the height so `body` reserves it, and take it back when the bar goes.
  useEffect(() => {
    const el = ref.current;
    if (!el) {
      document.documentElement.style.setProperty("--view-as-banner-h", "0px");
      return;
    }
    const ro = new ResizeObserver(() => {
      document.documentElement.style.setProperty("--view-as-banner-h", `${el.offsetHeight}px`);
    });
    ro.observe(el);
    document.documentElement.style.setProperty("--view-as-banner-h", `${el.offsetHeight}px`);
    return () => {
      ro.disconnect();
      document.documentElement.style.setProperty("--view-as-banner-h", "0px");
    };
  }, [viewing, ended]);

  if (ended) {
    return (
      <Ended ref={ref}>
        <Who>You are no longer viewing this account.</Who>
      </Ended>
    );
  }

  if (!viewing) return null;

  return (
    <Bar ref={ref}>
      <Who>
        Viewing account <strong>{viewingEmail() ?? "a customer"}</strong>
      </Who>
      <Exit
        type="button"
        onClick={() => {
          endView();
          setViewing(false);
          setEnded(true);
        }}
      >
        EXIT
      </Exit>
    </Bar>
  );
}
